import { useCallback, useState } from "react";

const useHoverEffects = (nodes, setNodes, edges, setEdges, selectedEdge) => {
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);

  const onNodeMouseEnter = useCallback(
    (event, node) => {
      if (selectedEdge) return;

      setNodes((nds) =>
        nds.map((n) => {
          if (!n.originalStyle) {
            n.originalStyle = { ...n.style };
          }

          if (n.id === node.id) {
            return {
              ...n,
              style: {
                ...n.style,
                opacity: 1,
              },
            };
          }

          const isConnected = edges.some(
            (edge) =>
              (edge.source === node.id && edge.target === n.id) ||
              (edge.target === node.id && edge.source === n.id)
          );

          if (isConnected) {
            return {
              ...n,
              style: {
                ...n.style,
                opacity: 1,
              },
            };
          }

          return {
            ...n,
            style: {
              ...n.style,
              opacity: 0.3,
            },
          };
        })
      );

      setEdges((eds) =>
        eds.map((ed) => {
          // Ensure originalColor is set when initially applying styles
          if (!ed.originalColor) {
            ed.originalColor = ed.style?.stroke || "#96EF48"; // Default color if stroke is not set
          }
          if (!ed.originalStyle) {
            ed.originalStyle = { ...ed.style };
          }

          if (ed.source === node.id || ed.target === node.id) {
            return {
              ...ed,
              animated: true,
              style: {
                ...ed.style,
                stroke: ed.originalColor,
                strokeWidth: 3,
                opacity: 1,
              },
            };
          }
          return {
            ...ed,
            style: {
              ...ed.style,
              stroke: "gray",
              opacity: 0.3,
            },
          };
        })
      );
    },
    [edges, setEdges, setNodes, selectedEdge]
  );

  const onNodeMouseLeave = useCallback(() => {
    if (selectedEdge) return;

    setHoveredNodeId(null);
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        style: n.originalStyle || {
          ...n.style,
          opacity: 1, // Reset to default or original style
        },
      }))
    );

    setEdges((eds) =>
      eds.map((ed) => ({
        ...ed,
        animated: false,
        style: ed.originalStyle || {
          ...ed.style,
          stroke: ed.originalColor, // Restore the original color
          strokeWidth: 2,
          opacity: 1, // Reset opacity of all edges
        },
      }))
    );
  }, [setNodes, setEdges, selectedEdge]);

  const onEdgeMouseEnter = useCallback(
    (event, edge) => {
      if (selectedEdge) return;

      setHoveredEdgeId(edge.id);

      setNodes((nds) =>
        nds.map((node) => {
          // Ensure originalStyle is set when initially applying styles
          if (!node.originalStyle) {
            node.originalStyle = { ...node.style };
          }

          if (node.id === edge.source || node.id === edge.target) {
            return {
              ...node,
              style: {
                ...node.style,
                opacity: 1,
              },
            };
          }
          return {
            ...node,
            style: {
              ...node.style,
              opacity: 0.3,
            },
          };
        })
      );

      setEdges((eds) =>
        eds.map((ed) => {
          // Ensure originalColor is set when initially applying styles
          if (!ed.originalColor) {
            ed.originalColor = ed.style?.stroke || "#96EF48"; // Default color if stroke is not set
          }
          if (!ed.originalStyle) {
            ed.originalStyle = { ...ed.style };
          }

          if (ed.id === edge.id) {
            return {
              ...ed,
              animated: true,
              style: {
                ...ed.style,
                stroke: ed.originalColor,
                strokeWidth: 3,
                opacity: 1,
              },
            };
          }
          return {
            ...ed,
            style: {
              ...ed.style,
              stroke: "gray",
              opacity: 0.3,
            },
          };
        })
      );
    },
    [setNodes, setEdges, selectedEdge]
  );

  const onEdgeMouseLeave = useCallback(() => {
    if (selectedEdge) return;

    setHoveredEdgeId(null);
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        style: node.originalStyle || {
          ...node.style,
          opacity: 1, // Reset to default or original style
        },
      }))
    );

    setEdges((eds) =>
      eds.map((ed) => ({
        ...ed,
        animated: false,
        style: ed.originalStyle || {
          ...ed.style,
          stroke: ed.originalColor, // Restore the original color
          strokeWidth: 2,
          opacity: 1, // Reset opacity of all edges
        },
      }))
    );
  }, [setNodes, setEdges, selectedEdge]);

  return {
    onNodeMouseEnter,
    onNodeMouseLeave,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
  };
};

export default useHoverEffects;
